import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProductionManagement } from '@/lib/production-management-context';
import { useSprints } from '@/lib/sprint-context';
import { assignTaskSprint } from '@/lib/notion-client';
import { NotionAvatar } from '@/components/notion-avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  RefreshCw, 
  Users, 
  TrendingUp, 
  DollarSign, 
  Star, 
  AlertTriangle,
  Calendar,
  Filter,
  ChevronLeft,
  ChevronRight,
  Plus,
  ChevronDown,
  Search,
  Building2,
  ChevronsUpDown,
  Check,
  ExternalLink,
  CalendarPlus
} from 'lucide-react';
import type { SprintTask, Sprint } from '@/lib/types';
import { getTagColors } from '@/lib/tag-colors';
import { getStatusColors } from '@/lib/status-colors';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const TASK_STATUS_OPTIONS = ['Backlog', 'Not Started', 'In Progress', 'Quality Check', 'Done'];
const TASK_PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

// Week calculation helpers
function getWeekNumber(date: Date): { week: number; year: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { week: weekNo, year: d.getUTCFullYear() };
}

function getWeekDateRange(weekNumber: number, year: number): { start: Date; end: Date } {
  const simple = new Date(year, 0, 1 + (weekNumber - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = simple;
  if (dow <= 4) {
    ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }
  const start = new Date(ISOweekStart);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start, end };
}

function formatWeekLabel(week: number, year: number): string {
  return `W${week.toString().padStart(2, '0')}.${year.toString().slice(-2)}`;
}

function formatDateRange(start: Date, end: Date): string {
  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startStr} - ${endStr}`;
}

function projectIntersectsWeek(
  project: any,
  weekStart: Date,
  weekEnd: Date,
  sprintTasks: SprintTask[],
  sprints: Sprint[]
): boolean {
  // Find all tasks for this project
  const projectTasks = sprintTasks.filter(task => task.projectId === project.id);
  
  if (projectTasks.length === 0) return false;
  
  // Check if any task's sprint intersects with this week
  return projectTasks.some(task => {
    return task.sprints.some(sprintId => {
      const sprint = sprints.find(s => s.id === sprintId);
      if (!sprint || !sprint.startDate || !sprint.endDate) return false;
      
      const sprintStart = new Date(sprint.startDate);
      const sprintEnd = new Date(sprint.endDate);
      
      return sprintStart <= weekEnd && sprintEnd >= weekStart;
    });
  });
}

export default function ProductionManagementDashboardPage() {
  const navigate = useNavigate();
  
  const { 
    resources, 
    projects, 
    users,
    loadingResources, 
    loadingProjects, 
    loadingUsers,
    errorResources, 
    errorProjects,
    refreshAll,
    refreshProjects,
    getUsersByIds,
    calculateRealizedBudget
  } = useProductionManagement();

  const {
    sprintTasks,
    sprints,
    loadingSprints,
    errorSprints,
    refreshSprints
  } = useSprints();

  const { toast } = useToast();

  const [refreshing, setRefreshing] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>(['TECH', 'CREA', 'MKT']);
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0); // 0 = current week
  const [showDoneProjects, setShowDoneProjects] = useState(false);
  const [showMaintenanceProjects, setShowMaintenanceProjects] = useState(true);
  const [isDepartmentFilterOpen, setIsDepartmentFilterOpen] = useState(false);
  
  // Add Project Dialog State
  const [isAddProjectDialogOpen, setIsAddProjectDialogOpen] = useState(false);
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null);
  const [isSearchingCompany, setIsSearchingCompany] = useState(false);
  const [companyNotFound, setCompanyNotFound] = useState(false);
  
  // Add Company Dialog State
  const [isAddCompanyDialogOpen, setIsAddCompanyDialogOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyEmail, setNewCompanyEmail] = useState('');
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState<any[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<any[]>([]);
  const [isSearchingContact, setIsSearchingContact] = useState(false);
  const [contactNotFound, setContactNotFound] = useState(false);
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  
  // Project Creation State
  const [projectCreationStep, setProjectCreationStep] = useState<'company' | 'project'>('company');
  const [projectName, setProjectName] = useState('');
  const [projectCode, setProjectCode] = useState('');
  const [projectBudget, setProjectBudget] = useState('');
  const [projectGDriveRoom, setProjectGDriveRoom] = useState('');
  const [projectStatus, setProjectStatus] = useState('Planning');
  const [projectPriority, setProjectPriority] = useState('Medium');
  const [projectStartDate, setProjectStartDate] = useState('');
  const [projectEndDate, setProjectEndDate] = useState('');
  const [projectOwner, setProjectOwner] = useState('');
  const [projectCollaborators, setProjectCollaborators] = useState<string[]>([]);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  
  // Add Contact Dialog State
  const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState(false);
  const [newContactFirstName, setNewContactFirstName] = useState('');
  const [newContactLastName, setNewContactLastName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [existingContactConflict, setExistingContactConflict] = useState<any>(null);

  // Add Task Dialog State
  const [isAddTaskDialogOpen, setIsAddTaskDialogOpen] = useState(false);
  const [taskProjectId, setTaskProjectId] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [taskStatus, setTaskStatus] = useState('Not Started');
  const [taskPriority, setTaskPriority] = useState('Medium');
  const [taskTags, setTaskTags] = useState<string[]>([]);
  const [taskSprintId, setTaskSprintId] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskAssignees, setTaskAssignees] = useState<string[]>([]);
  const [taskContributors, setTaskContributors] = useState<string[]>([]);
  const [taskEstimatedHours, setTaskEstimatedHours] = useState('');
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [isProjectPickerOpen, setIsProjectPickerOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  
  // Sprint Assignment Dialog State
  const [isSprintAssignDialogOpen, setIsSprintAssignDialogOpen] = useState(false);
  const [taskToAssign, setTaskToAssign] = useState<SprintTask | null>(null);
  const [selectedSprintForAssign, setSelectedSprintForAssign] = useState('');
  const [sprintSearchTerm, setSprintSearchTerm] = useState('');
  const [isAssigningSprint, setIsAssigningSprint] = useState(false);
  
  // Unassigned Tasks - track how many to show per project
  const [unassignedTasksLimit, setUnassignedTasksLimit] = useState<Record<string, number>>({});
  const DEFAULT_UNASSIGNED_LIMIT = 5;
  const LOAD_MORE_INCREMENT = 10;

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [projects]);

  const selectedProject = useMemo(() => {
    return sortedProjects.find(project => project.id === taskProjectId);
  }, [sortedProjects, taskProjectId]);

  const taskPickerProjects = useMemo(() => {
    const term = projectSearchTerm.trim().toLowerCase();
    if (!term) return sortedProjects;
    return sortedProjects.filter(project => {
      const name = (project.name || '').toLowerCase();
      const code = (project.projectCode || '').toLowerCase();
      return name.includes(term) || code.includes(term);
    });
  }, [projectSearchTerm, sortedProjects]);

  const sortedSprintsDesc = useMemo(() => {
    return [...sprints].sort((a, b) => {
      const aDate = a.startDate ? new Date(a.startDate).getTime() : 0;
      const bDate = b.startDate ? new Date(b.startDate).getTime() : 0;
      return bDate - aDate;
    });
  }, [sprints]);

  const resetTaskForm = () => {
    setTaskProjectId('');
    setTaskTitle('');
    setTaskStatus('Not Started');
    setTaskPriority('Medium');
    setTaskTags([]);
    setTaskSprintId('');
    setTaskDueDate('');
    setTaskAssignees([]);
    setTaskContributors([]);
    setTaskEstimatedHours('');
    setIsProjectPickerOpen(false);
    setProjectSearchTerm('');
  };

  const handleOpenAddTaskDialog = () => {
    if (!taskProjectId && sortedProjects.length > 0) {
      setTaskProjectId(sortedProjects[0].id);
    }
    const fallbackTag = selectedTags[0] || sprintTasks.find(task => Array.isArray(task.tags) && task.tags.length > 0)?.tags?.[0];
    const defaultTag = fallbackTag ? [fallbackTag] : [];
    setTaskTags(prev => (prev.length > 0 ? [prev[0]] : defaultTag));
    setIsAddTaskDialogOpen(true);
  };

  const handleTaskDialogChange = (open: boolean) => {
    setIsAddTaskDialogOpen(open);
    if (!open) {
      setIsProjectPickerOpen(false);
    }
    if (!open && !taskSubmitting) {
      resetTaskForm();
    }
  };

  const handleSelectProject = (projectId: string) => {
    setTaskProjectId(projectId);
    setIsProjectPickerOpen(false);
    setProjectSearchTerm('');
  };

  const toggleTaskTag = (tag: string) => {
    setTaskTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const toggleTaskContributor = (resourceId: string) => {
    setTaskContributors(prev => prev.includes(resourceId) ? prev.filter(id => id !== resourceId) : [...prev, resourceId]);
  };

  const toggleTaskAssignee = (email: string) => {
    setTaskAssignees(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  // Get all unique tags from sprint tasks
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    sprintTasks.forEach(task => {
      if (Array.isArray(task.tags)) {
        task.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [sprintTasks]);

  // Filter projects by selected tags (using sprint tasks tags) and status
  const filteredProjects = useMemo(() => {
    let filtered = projects;
    
    // Filter by status (Done/Maintenance)
    filtered = filtered.filter(project => {
      const status = project.status?.toLowerCase() || '';
      
      // Filter out Done/Archived projects unless showDoneProjects is true
      if (!showDoneProjects && (status.includes('done') || status.includes('completed') || status.includes('finished') || status.includes('archived'))) {
        return false;
      }
      
      // Filter out Maintenance projects unless showMaintenanceProjects is true
      if (!showMaintenanceProjects && status.includes('maintenance')) {
        return false;
      }
      
      return true;
    });
    
    // Filter by tags if any selected
    if (selectedTags.length === 0) return filtered;
    
    return filtered.filter(project => {
      // Get all tasks for this project
      const projectTasks = sprintTasks.filter(task => task.projectId === project.id);
      
      // If project has no tasks, include it (new projects without tasks yet)
      if (projectTasks.length === 0) return true;
      
      // Check if any task has a selected tag
      return projectTasks.some(task => 
        Array.isArray(task.tags) && 
        task.tags.some(tag => selectedTags.includes(tag))
      );
    });
  }, [projects, sprintTasks, selectedTags, showDoneProjects, showMaintenanceProjects]);

  const filteredProjectIds = useMemo(() => {
    return new Set(filteredProjects.map(project => project.id));
  }, [filteredProjects]);

  // Get active resources filtered by selected tags
  const activeResources = useMemo(() => {
    const active = resources.filter(r => r.active || r.activeResource);
    
    if (selectedTags.length === 0) return active;
    
    // Filter resources by department matching selected tags
    return active.filter(resource => 
      Array.isArray(resource.department) && 
      resource.department.some(dept => selectedTags.includes(dept))
    );
  }, [resources, selectedTags]);

  // Calculate static KPIs (non-week-based)
  const staticKpis = useMemo(() => {
    // B - Budget Health: Calculate avg budget vs spent
    const projectsWithBudget = filteredProjects.filter(p => p.budget && p.spent !== undefined);
    const budgetHealth = projectsWithBudget.length > 0
      ? Math.round(
          projectsWithBudget.reduce((sum, p) => {
            const spent = p.spent || 0;
            const budget = p.budget || 1;
            return sum + ((budget - spent) / budget) * 100;
          }, 0) / projectsWithBudget.length
        )
      : 100;

    // Q - Quality Score: Based on progress vs timeline
    const projectsWithProgress = filteredProjects.filter(p => p.progress !== undefined);
    const qualityScore = projectsWithProgress.length > 0
      ? Math.round(
          projectsWithProgress.reduce((sum, p) => sum + (p.progress || 0), 0) / 
          projectsWithProgress.length
        )
      : 0;

    // T - Timeline Risk: Projects at risk (past due date or delayed)
    const now = new Date();
    const projectsAtRisk = filteredProjects.filter(p => {
      if (!p.endDate) return false;
      try {
        const endDate = new Date(p.endDate);
        const progress = p.progress || 0;
        // At risk if past end date or if progress is significantly behind
        return endDate < now && progress < 100;
      } catch {
        return false;
      }
    }).length;
    const timelineRisk = filteredProjects.length > 0
      ? Math.round((projectsAtRisk / filteredProjects.length) * 100)
      : 0;

    return {
      budgetHealth,
      qualityScore,
      timelineRisk
    };
  }, [filteredProjects]);



  // Timeline data for 3 weeks based on sprints
  const weeklyTimeline = useMemo(() => {
    const weekProjects: Array<{
      weekIndex: number;
      week: number;
      year: number;
      start: Date;
      end: Date;
      projects: any[];
      tagMetrics: Record<string, { capacity: number; remainingHours: number; utilization: number }>;
      budgetHealth: number; // Weighted budget health for the week
      totalBudget: number;
      totalRealized: number;
      projectsWithBudget: number;
    }> = [];

    for (let weekIndex = 0; weekIndex < 3; weekIndex++) {
      const today = new Date();
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + (currentWeekOffset + weekIndex) * 7);
      
      const { week, year } = getWeekNumber(targetDate);
      const { start, end } = getWeekDateRange(week, year);
      
      let weekFilteredProjects = filteredProjects.filter(project => 
        projectIntersectsWeek(project, start, end, sprintTasks, sprints)
      );

      // If tags are selected, further filter to only show projects that have tasks with those tags in THIS specific week
      if (selectedTags.length > 0) {
        weekFilteredProjects = weekFilteredProjects.filter(project => {
          // Get tasks for this project in this specific week
          const projectTasksInWeek = sprintTasks.filter(task => {
            if (task.projectId !== project.id) return false;
            
            // Check if task's sprints intersect with this week
            return task.sprints.some(sprintId => {
              const sprint = sprints.find(s => s.id === sprintId);
              if (!sprint || !sprint.startDate || !sprint.endDate) return false;
              
              const sprintStart = new Date(sprint.startDate);
              const sprintEnd = new Date(sprint.endDate);
              
              return sprintStart <= end && sprintEnd >= start;
            });
          });
          
          // Check if any of these week-specific tasks have the selected tags
          return projectTasksInWeek.some(task => 
            Array.isArray(task.tags) && 
            task.tags.some(tag => selectedTags.includes(tag))
          );
        });
      }

      // Find all tasks in this week (exclude Done/Archived)
      const weekTasks = sprintTasks.filter(task => {
        if (task.projectId && !filteredProjectIds.has(task.projectId)) {
          return false;
        }
        const taskStatus = task.status?.toLowerCase() || '';
        if (taskStatus.includes('done') || taskStatus.includes('archived') || taskStatus.includes('completed')) {
          return false;
        }
        
        // Check if task's sprints intersect with this week
        return task.sprints.some(sprintId => {
          const sprint = sprints.find(s => s.id === sprintId);
          if (!sprint || !sprint.startDate || !sprint.endDate) return false;
          
          const sprintStart = new Date(sprint.startDate);
          const sprintEnd = new Date(sprint.endDate);
          
          return sprintStart <= end && sprintEnd >= start;
        });
      });

      // Calculate capacity and utilization PER TAG for this week
      const tagMetrics: Record<string, { capacity: number; remainingHours: number; utilization: number }> = {};
      
      // Get all unique tags from week tasks
      const weekTags = new Set<string>();
      weekTasks.forEach(task => {
        if (Array.isArray(task.tags)) {
          task.tags.forEach(tag => weekTags.add(tag));
        }
      });

      // Calculate metrics for each tag
      weekTags.forEach(tag => {
        // Find all active resources with this tag in their department
        const tagResources = activeResources.filter(r => 
          Array.isArray(r.department) && r.department.includes(tag)
        );

        // Calculate total capacity for this tag
        const tagCapacity = tagResources.reduce((sum, r) => {
          const capacity = typeof r.weeklyCapacity === 'string' 
            ? parseFloat(r.weeklyCapacity) 
            : (r.weeklyCapacity || 0);
          return sum + capacity;
        }, 0);

        // Calculate remaining hours for tasks with this tag
        const tagRemainingHours = weekTasks.reduce((sum, task) => {
          if (Array.isArray(task.tags) && task.tags.includes(tag)) {
            const estimated = task.estimatedHours || 0;
            const realised = task.realisedHours || 0;
            return sum + Math.max(0, estimated - realised);
          }
          return sum;
        }, 0);

        // Calculate utilization for this tag
        const tagUtilization = tagCapacity > 0 
          ? Math.round((tagRemainingHours / tagCapacity) * 100) 
          : 0;

        tagMetrics[tag] = {
          capacity: tagCapacity,
          remainingHours: tagRemainingHours,
          utilization: tagUtilization
        };
      });

      // Calculate weighted budget health for this week
      // Only include projects that have a budget set
      let totalBudget = 0;
      let weightedBudgetHealthSum = 0;
      let projectsWithBudget = 0;
      let totalRealized = 0;
      
      weekFilteredProjects.forEach(project => {
        const projectBudget = typeof project.budget === 'number' 
          ? project.budget 
          : (typeof project.budget === 'string' ? parseFloat(project.budget) : 0);
        
        if (projectBudget > 0) {
          // Get all task IDs for this project
          const projectTaskIds = sprintTasks
            .filter(task => task.projectId === project.id)
            .map(task => task.id);
          
          const budgetBreakdown = calculateRealizedBudget(projectTaskIds);
          const realized = budgetBreakdown.totalRealized;
          
          // Budget health = (budget - realized) / budget * 100
          // Clamp between 0 and 100
          const projectBudgetHealth = Math.max(0, Math.min(100, Math.round(((projectBudget - realized) / projectBudget) * 100)));
          
          // Weight by budget size
          weightedBudgetHealthSum += projectBudgetHealth * projectBudget;
          totalBudget += projectBudget;
          totalRealized += realized;
          projectsWithBudget++;
        }
      });
      
      // Calculate weighted average budget health
      const weekBudgetHealth = totalBudget > 0 
        ? Math.round(weightedBudgetHealthSum / totalBudget)
        : 100; // Default to 100% if no projects have budgets

      weekProjects.push({
        weekIndex,
        week,
        year,
        start,
        end,
        projects: weekFilteredProjects,
        tagMetrics,
        budgetHealth: weekBudgetHealth,
        totalBudget,
        totalRealized,
        projectsWithBudget
      });
    }

    return weekProjects;
  }, [filteredProjects, sprintTasks, sprints, currentWeekOffset, activeResources, selectedTags, calculateRealizedBudget]);

  // Calculate dynamic KPIs based on the 3 displayed weeks - PER TAG
  const dynamicKpis = useMemo(() => {
    // Aggregate metrics across all 3 weeks per tag
    const tagAggregates: Record<string, { capacity: number; remainingHours: number; utilization: number }> = {};
    
    weeklyTimeline.forEach(week => {
      Object.entries(week.tagMetrics).forEach(([tag, metrics]) => {
        if (!tagAggregates[tag]) {
          tagAggregates[tag] = { capacity: 0, remainingHours: 0, utilization: 0 };
        }
        tagAggregates[tag].capacity += metrics.capacity;
        tagAggregates[tag].remainingHours += metrics.remainingHours;
      });
    });

    // Calculate utilization for each tag across 3 weeks
    Object.keys(tagAggregates).forEach(tag => {
      const agg = tagAggregates[tag];
      agg.utilization = agg.capacity > 0 
        ? Math.round((agg.remainingHours / agg.capacity) * 100) 
        : 0;
    });

    // Calculate weighted budget health across all 3 weeks
    // Use total budgets and realized amounts, weighted by budget size
    let totalBudgetAcrossWeeks = 0;
    let weightedBudgetHealthSum = 0;
    
    weeklyTimeline.forEach(week => {
      if (week.totalBudget > 0) {
        // Calculate this week's budget health contribution weighted by its total budget
        weightedBudgetHealthSum += week.budgetHealth * week.totalBudget;
        totalBudgetAcrossWeeks += week.totalBudget;
      }
    });
    
    const overallBudgetHealth = totalBudgetAcrossWeeks > 0 
      ? Math.round(weightedBudgetHealthSum / totalBudgetAcrossWeeks)
      : 100; // Default to 100% if no projects have budgets

    return {
      tagMetrics: tagAggregates,
      budgetHealth: overallBudgetHealth,
      qualityScore: staticKpis.qualityScore,
      timelineRisk: staticKpis.timelineRisk
    };
  }, [weeklyTimeline, staticKpis]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      refreshAll(true),
      refreshSprints(true)
    ]);
    setRefreshing(false);
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Search for company
  const searchCompany = async () => {
    if (!companySearchQuery.trim()) return;
    
    setIsSearchingCompany(true);
    setCompanyNotFound(false);
    setCompanySearchResults([]);
    
    try {
      const config = (window as any).WP_COCKPIT;
      if (!config) throw new Error('WP Cockpit config not available');
      
      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/projects/management/company`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce;
      
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({ name: companySearchQuery }),
      });
      
      if (res.status === 404) {
        setCompanyNotFound(true);
      } else if (!res.ok) {
        throw new Error(`Failed to search company: ${res.status}`);
      } else {
        const data = await res.json();
        setCompanySearchResults(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error searching company:', err);
    } finally {
      setIsSearchingCompany(false);
    }
  };

  const handleAddCompanyToNotion = () => {
    setIsAddCompanyDialogOpen(true);
    setIsAddProjectDialogOpen(false);
    setNewCompanyName(companySearchQuery);
    setNewCompanyEmail('');
    setContactSearchQuery('');
    setContactSearchResults([]);
    setSelectedContacts([]);
    setContactNotFound(false);
  };

  const handleOpenAddProjectDialog = () => {
    setIsAddProjectDialogOpen(true);
    setCompanySearchQuery('');
    setCompanySearchResults([]);
    setSelectedCompany(null);
    setCompanyNotFound(false);
  };

  // Search for contact
  const searchContact = async () => {
    if (!contactSearchQuery.trim()) return;
    
    setIsSearchingContact(true);
    setContactNotFound(false);
    setContactSearchResults([]);
    
    try {
      const config = (window as any).WP_COCKPIT;
      if (!config) throw new Error('WP Cockpit config not available');
      
      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/projects/management/contact`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce;
      
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({ name: contactSearchQuery }),
      });
      
      if (res.status === 404) {
        setContactNotFound(true);
      } else if (!res.ok) {
        throw new Error(`Failed to search contact: ${res.status}`);
      } else {
        const data = await res.json();
        setContactSearchResults(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error searching contact:', err);
    } finally {
      setIsSearchingContact(false);
    }
  };

  const handleAddContactToNotion = () => {
    setIsAddContactDialogOpen(true);
  };

  const handleCreateContact = async () => {
    if (!newContactFirstName || !newContactLastName || !newContactEmail) {
      return;
    }

    setIsCreatingContact(true);
    setExistingContactConflict(null);

    try {
      const config = (window as any).WP_COCKPIT;
      if (!config) throw new Error('WP Cockpit config not available');
      
      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/projects/management/contact`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce;
      
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({
          action: 'CREATE',
          firstName: newContactFirstName,
          lastName: newContactLastName,
          email: newContactEmail,
          phone: newContactPhone || undefined
        })
      });

      const data = await response.json();

      // Handle 409 conflict - email already in use
      if (response.status === 409) {
        // Webhook returns a plain array of contacts on conflict
        if (Array.isArray(data) && data.length > 0) {
          setExistingContactConflict(data[0]);
        }
        setIsCreatingContact(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to create contact');
      }
      
      // Success response - webhook returns an array with the created contact
      if (Array.isArray(data) && data.length > 0) {
        // Add the newly created contact to selected contacts
        setSelectedContacts(prev => [...prev, data[0]]);
        
        // Reset form and close dialog
        setNewContactFirstName('');
        setNewContactLastName('');
        setNewContactEmail('');
        setNewContactPhone('');
        setIsAddContactDialogOpen(false);
        setContactNotFound(false);
        setExistingContactConflict(null);
      }
    } catch (error) {
      console.error('Error creating contact:', error);
    } finally {
      setIsCreatingContact(false);
    }
  };

  const handleSelectExistingContact = () => {
    if (existingContactConflict) {
      setSelectedContacts(prev => [...prev, existingContactConflict]);
      
      // Reset form and close dialog
      setNewContactFirstName('');
      setNewContactLastName('');
      setNewContactEmail('');
      setNewContactPhone('');
      setIsAddContactDialogOpen(false);
      setContactNotFound(false);
      setExistingContactConflict(null);
    }
  };

  const handleSelectContact = (contact: any) => {
    setSelectedContacts(prev => {
      const exists = prev.find(c => c.id === contact.id);
      if (exists) {
        return prev.filter(c => c.id !== contact.id);
      }
      return [...prev, contact];
    });
  };

  const handleSubmitCompany = async () => {
    if (!newCompanyName.trim() || !newCompanyEmail.trim() || selectedContacts.length === 0) {
      return;
    }

    setIsCreatingCompany(true);

    try {
      const config = (window as any).WP_COCKPIT;
      if (!config) throw new Error('WP Cockpit config not available');
      
      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/projects/management/company`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce;
      
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({
          action: 'CREATE',
          name: newCompanyName,
          email: newCompanyEmail,
          contactIds: selectedContacts.map(c => c.id)
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error('Failed to create company');
      }
      
      // Success response - webhook returns an array with the created company
      if (Array.isArray(data) && data.length > 0) {
        // Company created successfully - move to project creation step
        setSelectedCompany(data[0]);
        setProjectCreationStep('project');
        
        // Reset company form
        setNewCompanyName('');
        setNewCompanyEmail('');
        setSelectedContacts([]);
        setContactSearchQuery('');
        setContactSearchResults([]);
        setContactNotFound(false);
      }
    } catch (error) {
      console.error('Error creating company:', error);
    } finally {
      setIsCreatingCompany(false);
    }
  };

  const handleSelectCompany = (company: any) => {
    setSelectedCompany(company);
    setProjectCreationStep('project');
  };

  const handleCreateProject = async () => {
    if (!projectName.trim() || !projectCode.trim() || !projectBudget.trim() || !selectedCompany || !projectOwner) {
      return;
    }

    setIsCreatingProject(true);

    try {
      const config = (window as any).WP_COCKPIT;
      if (!config) throw new Error('WP Cockpit config not available');
      
      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/projects/management/project`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce;
      
      const payload: any = {
        action: 'CREATE',
        name: projectName,
        projectCode: projectCode,
        budget: parseFloat(projectBudget),
        status: projectStatus,
        companyId: selectedCompany.id
      };

      if (projectGDriveRoom.trim()) {
        payload.gdriveRoom = projectGDriveRoom;
      }
      
      if (projectPriority) {
        payload.priority = projectPriority;
      }
      
      if (projectStartDate) {
        payload.startDate = projectStartDate;
      }
      
      if (projectEndDate) {
        payload.endDate = projectEndDate;
      }
      
      if (projectOwner) {
        payload.owner = projectOwner;
      }
      
      if (projectCollaborators.length > 0) {
        payload.collaborators = projectCollaborators;
      }
      
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error('Failed to create project');
      }
      
      // Success response - webhook returns an array with the created project
      if (Array.isArray(data) && data.length > 0) {
        // Project created successfully - refresh projects to get the new one
        await refreshProjects(true);
        
        // Reset form and close dialog
        setProjectName('');
        setProjectCode('');
        setProjectBudget('');
        setProjectGDriveRoom('');
        setProjectStatus('Planning');
        setProjectPriority('Medium');
        setProjectStartDate('');
        setProjectEndDate('');
        setProjectOwner('');
        setProjectCollaborators([]);
        setSelectedCompany(null);
        setProjectCreationStep('company');
        setIsAddProjectDialogOpen(false);
      }
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setIsCreatingProject(false);
    }
  };

  const handleCreateTask = async () => {
    if (!taskTitle.trim() || !taskProjectId) {
      return;
    }

    setTaskSubmitting(true);

    try {
      const config = (window as any).WP_COCKPIT;
      if (!config) throw new Error('WP Cockpit config not available');

      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/projects/management/task`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce;

      const payload: Record<string, unknown> = {
        action: 'CREATE',
        title: taskTitle.trim(),
        projectId: taskProjectId,
        status: taskStatus,
      };

      if (taskPriority) payload.priority = taskPriority;
      if (taskTags.length > 0) payload.tags = taskTags;
      if (taskSprintId) payload.sprints = [taskSprintId];
      if (taskDueDate) payload.dueDate = taskDueDate;
      if (taskAssignees.length > 0) payload.assignee = taskAssignees;
      if (taskContributors.length > 0) payload.contributors = taskContributors;
      if (taskEstimatedHours.trim()) payload.estimatedHours = parseFloat(taskEstimatedHours);

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify(payload)
      });

      let data: any = null;
      try {
        data = await response.json();
      } catch (err) {
        if ((window as any).WP_COCKPIT?.devMode) {
          console.warn('Add Task response parsing failed:', err);
        }
      }

      if (!response.ok) {
        const apiMessage = data?.message || data?.error || 'Failed to create task';
        throw new Error(apiMessage);
      }

      await refreshSprints(true);
      toast({
        title: 'Task created',
        description: `${taskTitle.trim()} is now queued for the team.`,
      });
      resetTaskForm();
      setIsAddTaskDialogOpen(false);
    } catch (error) {
      console.error('Error creating task:', error);
      toast({
        title: 'Unable to create task',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setTaskSubmitting(false);
    }
  };

  const loading = loadingResources || loadingProjects || loadingSprints;
  const error = errorResources || errorProjects || errorSprints;

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Production Management Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Overview of your projects, resources, and key metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => setShowDoneProjects(!showDoneProjects)}
            variant={showDoneProjects ? "default" : "outline"}
            size="sm"
          >
            {showDoneProjects ? "Hide" : "Show"} Done/Archived
          </Button>
          <Button 
            onClick={() => setShowMaintenanceProjects(!showMaintenanceProjects)}
            variant={showMaintenanceProjects ? "outline" : "default"}
            size="sm"
          >
            {showMaintenanceProjects ? "Hide" : "Show"} Maintenance
          </Button>
          <Button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            variant="outline"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tag Filters */}
      {allTags.length > 0 && (
        <Collapsible open={isDepartmentFilterOpen} onOpenChange={setIsDepartmentFilterOpen}>
          <Card>
            <CollapsibleTrigger className="w-full">
              <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Filter className="h-5 w-5" />
                    Filter by Department
                    {selectedTags.length > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {selectedTags.length} selected
                      </Badge>
                    )}
                  </div>
                  <ChevronDown className={`h-5 w-5 transition-transform ${isDepartmentFilterOpen ? 'rotate-180' : ''}`} />
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {allTags.map(tag => {
                    const tagColors = getTagColors(tag);
                    const isSelected = selectedTags.includes(tag);
                    return (
                      <Badge
                        key={tag}
                        className={`cursor-pointer border ${
                          isSelected 
                            ? `${tagColors.bg} ${tagColors.text} ${tagColors.border}` 
                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={() => toggleTag(tag)}
                      >
                        {tag}
                      </Badge>
                    );
                  })}
                  {selectedTags.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedTags([])}
                      className="h-6"
                    >
                      Clear all
                    </Button>
                  )}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* KPI Cards - Per Tag Capacity and Utilization */}
      <div className="grid grid-cols-1 gap-4">
        {/* Per-Tag Capacity and Utilization */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              3-Week Capacity & Utilization by Department
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="h-8 w-8" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {Object.entries(dynamicKpis.tagMetrics)
                  .filter(([tag]) => selectedTags.length === 0 || selectedTags.includes(tag))
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([tag, metrics]) => {
                    const tagColors = getTagColors(tag);
                    return (
                      <Card key={tag} className="border-2">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <Badge 
                              className={`text-sm font-semibold ${tagColors.bg} ${tagColors.text} ${tagColors.border} border`}
                            >
                              {tag}
                            </Badge>
                            <div className={`text-2xl font-bold ${
                              metrics.utilization > 100 ? 'text-red-600' :
                              metrics.utilization >= 80 ? 'text-orange-600' :
                              'text-green-600'
                            }`}>
                              {metrics.utilization}%
                            </div>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">
                              Capacity: {(metrics.capacity / 8).toFixed(1)}d ({metrics.capacity}h)
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Remaining: {(metrics.remainingHours / 8).toFixed(2)}d ({metrics.remainingHours.toFixed(2)}h)
                            </p>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                              <div 
                                className={`rounded-full h-2 transition-all ${
                                  metrics.utilization > 100 ? 'bg-red-500' :
                                  metrics.utilization >= 80 ? 'bg-orange-500' :
                                  'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(metrics.utilization, 100)}%` }}
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Other KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Budget Health */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Budget Health</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Spinner className="h-6 w-6" />
              ) : (
                <>
                  <div className={`text-2xl font-bold ${
                    dynamicKpis.budgetHealth >= 70 ? 'text-green-600' : 
                    dynamicKpis.budgetHealth >= 40 ? 'text-yellow-600' : 
                    'text-red-600'
                  }`}>
                    {dynamicKpis.budgetHealth}%
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Average remaining budget
                  </p>
                </>
              )}
            </CardContent>
          </Card>

        {/* Quality Score */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Quality Score</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Spinner className="h-6 w-6" />
            ) : (
              <>
                <div className="text-2xl font-bold">{dynamicKpis.qualityScore}%</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Average progress
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Timeline Risk */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Timeline Risk</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Spinner className="h-6 w-6" />
            ) : (
              <>
                <div className={`text-2xl font-bold ${
                  dynamicKpis.timelineRisk <= 20 ? 'text-green-600' : 
                  dynamicKpis.timelineRisk <= 50 ? 'text-yellow-600' : 
                  'text-red-600'
                }`}>
                  {dynamicKpis.timelineRisk}%
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Projects at risk
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      </div>

      {/* Timeline View */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Project Timeline
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="default" 
                size="sm"
                onClick={handleOpenAddTaskDialog}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Task
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleOpenAddProjectDialog}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Project
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentWeekOffset(currentWeekOffset - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentWeekOffset(0)}
              >
                Current
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentWeekOffset(currentWeekOffset + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner className="h-8 w-8" />
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              {weeklyTimeline.map((weekData) => {
                const weekLabel = formatWeekLabel(weekData.week, weekData.year);
                const dateRange = formatDateRange(weekData.start, weekData.end);
                const isCurrentWeek = currentWeekOffset + weekData.weekIndex === 0;
                
                return (
                  <div 
                    key={weekData.weekIndex}
                    className={`border rounded-lg p-4 ${
                      isCurrentWeek 
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                        : 'border-border'
                    }`}
                  >
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-lg">{weekLabel}</h3>
                        <div className="flex items-center gap-2">
                          {weekData.projectsWithBudget > 0 && (
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                weekData.budgetHealth >= 70 ? 'border-green-500 text-green-700 bg-green-50' :
                                weekData.budgetHealth >= 40 ? 'border-orange-500 text-orange-700 bg-orange-50' :
                                'border-red-500 text-red-700 bg-red-50'
                              }`}
                            >
                              <DollarSign className="h-3 w-3 mr-1" />
                              {weekData.budgetHealth}%
                            </Badge>
                          )}
                          {isCurrentWeek && (
                            <Badge variant="default" className="text-xs">Current</Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">{dateRange}</p>
                      
                      {/* Week Capacity and Utilization - Per Tag */}
                      <div className="pt-2 border-t space-y-2">
                        {Object.entries(weekData.tagMetrics)
                          .filter(([tag]) => selectedTags.length === 0 || selectedTags.includes(tag))
                          .sort((a, b) => a[0].localeCompare(b[0]))
                          .map(([tag, metrics]) => {
                            const tagColors = getTagColors(tag);
                            return (
                              <div key={tag} className="flex items-center justify-between gap-2">
                                <Badge 
                                  className={`text-xs ${tagColors.bg} ${tagColors.text} ${tagColors.border} border`}
                                >
                                  {tag}
                                </Badge>
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-1">
                                    <Users className="h-3 w-3 text-muted-foreground" />
                                    <span className="text-xs">{(metrics.capacity / 8).toFixed(1)}d</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <TrendingUp className="h-3 w-3 text-muted-foreground" />
                                    <span className={`text-xs font-semibold ${
                                      metrics.utilization > 100 ? 'text-red-600' :
                                      metrics.utilization >= 80 ? 'text-orange-600' :
                                      'text-green-600'
                                    }`}>
                                      {metrics.utilization}%
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {weekData.projects.length === 0 ? (
                        <p className="text-sm text-muted-foreground italic">No projects this week</p>
                      ) : (
                        <>
                          <p className="text-sm font-medium mb-2">
                            {weekData.projects.length} project{weekData.projects.length !== 1 ? 's' : ''}
                          </p>
                          <div className="space-y-2 max-h-[500px] overflow-y-auto">
                            {weekData.projects.map(project => {
                              // Calculate hours by tag for this project (exclude Done/Archived tasks)
                              // IMPORTANT: Only count tasks that are actually in this specific week
                              const projectTasks = sprintTasks.filter(task => {
                                const taskStatus = task.status?.toLowerCase() || '';
                                if (task.projectId !== project.id) return false;
                                if (taskStatus.includes('done') || taskStatus.includes('archived') || taskStatus.includes('completed')) {
                                  return false;
                                }
                                
                                // Check if task's sprints intersect with this specific week
                                return task.sprints.some(sprintId => {
                                  const sprint = sprints.find(s => s.id === sprintId);
                                  if (!sprint || !sprint.startDate || !sprint.endDate) return false;
                                  
                                  const sprintStart = new Date(sprint.startDate);
                                  const sprintEnd = new Date(sprint.endDate);
                                  
                                  return sprintStart <= weekData.end && sprintEnd >= weekData.start;
                                });
                              });
                              const projectHoursByTag: Record<string, { estimated: number, realised: number }> = {};
                              
                              projectTasks.forEach(task => {
                                if (task.tags && task.tags.length > 0) {
                                  task.tags.forEach(tag => {
                                    if (!projectHoursByTag[tag]) {
                                      projectHoursByTag[tag] = { estimated: 0, realised: 0 };
                                    }
                                    projectHoursByTag[tag].estimated += task.estimatedHours || 0;
                                    projectHoursByTag[tag].realised += task.realisedHours || 0;
                                  });
                                }
                              });
                              
                              return (
                                <Link 
                                  key={project.id} 
                                  to={`/production-management/projects/${project.id}`}
                                  className="block"
                                >
                                  <Card className="hover:shadow-md transition-shadow border-l-4" style={{ borderLeftColor: getStatusColors(project.status || "").borderColorValue }}>
                                    <CardContent className="p-3">
                                      <div className="flex items-start justify-between gap-2 mb-2">
                                        <h4 className="text-sm font-semibold line-clamp-2 flex-1">
                                          {project.name}
                                        </h4>
                                        {project.status && (
                                          <Badge variant={undefined} className={`text-xs shrink-0 whitespace-nowrap ${getStatusColors(project.status).bg} ${getStatusColors(project.status).text} ${getStatusColors(project.status).border} border`}>
                                            {project.status}
                                          </Badge>
                                        )}
                                      </div>
                                      {project.projectCode && (
                                        <p className="text-xs text-muted-foreground mb-2">
                                          {project.projectCode}
                                        </p>
                                      )}
                                      
                                      {/* Owner and Collaborators */}
                                      {(() => {
                                        const owners = getUsersByIds(project.owner);
                                        const collaborators = getUsersByIds(project.collaborators);
                                        const owner = owners.length > 0 ? owners[0] : null;
                                        
                                        if (!owner && collaborators.length === 0) return null;
                                        
                                        return (
                                          <div className="flex flex-wrap gap-2 mb-2">
                                            {owner && (
                                              <div className="flex items-center gap-1 text-xs">
                                                <NotionAvatar 
                                                  name={owner.name}
                                                  profileImage={owner.profileImage}
                                                  className="h-4 w-4"
                                                />
                                                <span className="font-medium text-muted-foreground">{owner.name}</span>
                                              </div>
                                            )}
                                            {collaborators.length > 0 && (
                                              <div className="flex items-center gap-1">
                                                {collaborators.slice(0, 3).map((collab) => (
                                                  <NotionAvatar 
                                                    key={collab.id}
                                                    name={collab.name}
                                                    profileImage={collab.profileImage}
                                                    className="h-4 w-4"
                                                    alt={collab.name}
                                                  />
                                                ))}
                                                {collaborators.length > 3 && (
                                                  <span className="text-xs text-muted-foreground">+{collaborators.length - 3}</span>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                      
                                      {Object.keys(projectHoursByTag).length > 0 && (
                                        <div className="space-y-1.5">
                                          {Object.entries(projectHoursByTag).map(([tag, hours]) => {
                                            const tagColors = getTagColors(tag);
                                            const estimatedDays = (hours.estimated / 8).toFixed(1);
                                            const realisedDays = (hours.realised / 8).toFixed(1);
                                            const percentage = hours.estimated > 0 ? Math.round((hours.realised / hours.estimated) * 100) : 0;
                                            return (
                                              <div key={tag} className="space-y-1">
                                                <div className="flex items-center justify-between">
                                                  <Badge 
                                                    className={`text-xs px-2 py-0.5 ${tagColors.bg} ${tagColors.text} ${tagColors.border} border`}
                                                  >
                                                    {tag}
                                                  </Badge>
                                                  <span className="text-xs font-medium">
                                                    {realisedDays}d / {estimatedDays}d
                                                  </span>
                                                </div>
                                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                  <div 
                                                    className={`rounded-full h-1.5 transition-all ${
                                                      percentage > 100 ? 'bg-red-500' :
                                                      percentage >= 80 ? 'bg-orange-500' :
                                                      'bg-green-500'
                                                    }`}
                                                    style={{ width: `${Math.min(percentage, 100)}%` }}
                                                  />
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                      
                                      {/* Budget Health */}
                                      {(() => {
                                        // Get all task IDs for this project (including all statuses for budget calculation)
                                        const allProjectTaskIds = sprintTasks
                                          .filter(task => task.projectId === project.id)
                                          .map(task => task.id);
                                        
                                        if (allProjectTaskIds.length === 0) return null;
                                        
                                        const budgetBreakdown = calculateRealizedBudget(allProjectTaskIds);
                                        const realizedBudget = budgetBreakdown.totalRealized;
                                        const soldBudget = typeof project.budget === 'number' 
                                          ? project.budget 
                                          : (typeof project.budget === 'string' ? parseFloat(project.budget) : 0);
                                        
                                        // No budget sold - show warning
                                        if (!soldBudget || soldBudget === 0) {
                                          if (realizedBudget > 0) {
                                            return (
                                              <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                                                <AlertTriangle className="h-3 w-3" />
                                                <span>No budget set - ${realizedBudget.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} spent</span>
                                              </div>
                                            );
                                          }
                                          return null;
                                        }
                                        
                                        const budgetHealthPercent = Math.round((realizedBudget / soldBudget) * 100);
                                        const isOverBudget = budgetHealthPercent > 100;
                                        const isNearBudget = budgetHealthPercent >= 80;
                                        
                                        return (
                                          <div className="mt-2 space-y-1">
                                            <div className="flex items-center justify-between text-xs">
                                              <span className="flex items-center gap-1 text-muted-foreground">
                                                <DollarSign className="h-3 w-3" />
                                                Budget
                                              </span>
                                              <span className={`font-medium ${
                                                isOverBudget ? 'text-red-600' :
                                                isNearBudget ? 'text-orange-600' :
                                                'text-green-600'
                                              }`}>
                                                ${realizedBudget.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} / ${soldBudget.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ({budgetHealthPercent}%)
                                              </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                                              <div 
                                                className={`rounded-full h-1.5 transition-all ${
                                                  isOverBudget ? 'bg-red-500' :
                                                  isNearBudget ? 'bg-orange-500' :
                                                  'bg-green-500'
                                                }`}
                                                style={{ width: `${Math.min(budgetHealthPercent, 100)}%` }}
                                              />
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </CardContent>
                                  </Card>
                                </Link>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unassigned Tasks Section - Tasks without a sprint */}
      {(() => {
        const wpConfig = (window as any).WP_COCKPIT;
        const isDev = wpConfig?.devMode || false;
        
        // Debug logging
        if (isDev) {
          console.log('[UnassignedTasks] === DEBUG START ===');
          console.log('[UnassignedTasks] Total sprintTasks count:', sprintTasks.length);
          console.log('[UnassignedTasks] filteredProjectIds:', Array.from(filteredProjectIds));
          console.log('[UnassignedTasks] Sample sprintTasks (first 5):', sprintTasks.slice(0, 5).map(t => ({
            id: t.id,
            title: t.title,
            projectId: t.projectId,
            sprints: t.sprints,
            sprintsLength: t.sprints?.length,
            status: t.status
          })));
          
          // Count tasks by sprint status
          const tasksWithSprints = sprintTasks.filter(t => t.sprints && t.sprints.length > 0);
          const tasksWithoutSprints = sprintTasks.filter(t => !t.sprints || t.sprints.length === 0);
          console.log('[UnassignedTasks] Tasks WITH sprints:', tasksWithSprints.length);
          console.log('[UnassignedTasks] Tasks WITHOUT sprints:', tasksWithoutSprints.length);
          
          if (tasksWithoutSprints.length > 0) {
            console.log('[UnassignedTasks] Sample tasks without sprints:', tasksWithoutSprints.slice(0, 3).map(t => ({
              id: t.id,
              title: t.title,
              projectId: t.projectId,
              status: t.status
            })));
          }
        }
        
        // Find tasks that have no sprints assigned and belong to filtered projects
        const unassignedTasks = sprintTasks.filter(task => {
          // Check if task has no sprints assigned
          if (task.sprints && task.sprints.length > 0) return false;
          // Check if task belongs to a filtered project
          if (task.projectId && !filteredProjectIds.has(task.projectId)) return false;
          // Exclude Done/Archived/Completed tasks
          const taskStatus = task.status?.toLowerCase() || '';
          if (taskStatus.includes('done') || taskStatus.includes('archived') || taskStatus.includes('completed')) {
            return false;
          }
          return true;
        });

        if (isDev) {
          console.log('[UnassignedTasks] Final unassignedTasks count:', unassignedTasks.length);
          if (unassignedTasks.length > 0) {
            console.log('[UnassignedTasks] Unassigned tasks:', unassignedTasks.map(t => ({
              id: t.id,
              title: t.title,
              projectId: t.projectId,
              status: t.status
            })));
          }
          console.log('[UnassignedTasks] === DEBUG END ===');
        }

        if (unassignedTasks.length === 0) return null;

        // Group tasks by project
        const tasksByProject: Record<string, typeof unassignedTasks> = {};
        unassignedTasks.forEach(task => {
          const projectId = task.projectId || 'no-project';
          if (!tasksByProject[projectId]) {
            tasksByProject[projectId] = [];
          }
          tasksByProject[projectId].push(task);
        });

        return (
          <Card className="border-orange-200 bg-orange-50/50 dark:border-orange-800 dark:bg-orange-950/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
                <AlertTriangle className="h-5 w-5" />
                Unassigned Tasks ({unassignedTasks.length})
              </CardTitle>
              <p className="text-sm text-muted-foreground">Tasks without a sprint assignment</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(tasksByProject).map(([projectId, tasks]) => {
                  const project = projects.find(p => p.id === projectId);
                  return (
                    <div key={projectId} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">
                          {project?.name || 'No Project'}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {tasks.length} task{tasks.length !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                      <div className="grid gap-2 pl-6">
                        {(() => {
                          const limit = unassignedTasksLimit[projectId] || DEFAULT_UNASSIGNED_LIMIT;
                          const visibleTasks = tasks.slice(0, limit);
                          const remainingCount = tasks.length - limit;
                          
                          return (
                            <>
                              {visibleTasks.map(task => {
                                const statusColors = getStatusColors(task.status || '');
                                return (
                                  <div
                                    key={task.id}
                                    className="flex items-center justify-between p-2 rounded-md border bg-background transition-colors"
                                  >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <span className="text-sm truncate">{task.title}</span>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      {task.tags?.map(tag => {
                                        const tagColors = getTagColors(tag);
                                        return (
                                          <Badge
                                            key={tag}
                                            className={`text-xs ${tagColors.bg} ${tagColors.text} ${tagColors.border} border`}
                                          >
                                            {tag}
                                          </Badge>
                                        );
                                      })}
                                      {task.status && (
                                        <Badge
                                          className={`text-xs ${statusColors.bg} ${statusColors.text} ${statusColors.border} border`}
                                        >
                                          {task.status}
                                        </Badge>
                                      )}
                                      {task.estimatedHours && (
                                        <span className="text-xs text-muted-foreground">
                                          {(task.estimatedHours / 8).toFixed(1)}d
                                        </span>
                                      )}
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 px-2 text-xs"
                                        onClick={() => {
                                          setTaskToAssign(task);
                                          setSelectedSprintForAssign('');
                                          setSprintSearchTerm('');
                                          setIsSprintAssignDialogOpen(true);
                                        }}
                                      >
                                        <CalendarPlus className="h-3 w-3 mr-1" />
                                        Assign
                                      </Button>
                                      <Button
                                        variant="default"
                                        size="sm"
                                        className="h-7 px-2 text-xs"
                                        onClick={() => navigate(`/production-management/task/${task.id}`, { state: { task } })}
                                      >
                                        <ExternalLink className="h-3 w-3 mr-1" />
                                        Open
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                              {remainingCount > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-xs text-muted-foreground hover:text-foreground justify-start pl-2"
                                  onClick={() => {
                                    setUnassignedTasksLimit(prev => ({
                                      ...prev,
                                      [projectId]: limit + LOAD_MORE_INCREMENT
                                    }));
                                  }}
                                >
                                  +{remainingCount} more task{remainingCount !== 1 ? 's' : ''} - Click to load more
                                </Button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Active Resources Capacity Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Active Resources Capacity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner className="h-8 w-8" />
            </div>
          ) : activeResources.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No active resources found
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {activeResources.map(resource => {
                const capacity = typeof resource.weeklyCapacity === 'string'
                  ? parseFloat(resource.weeklyCapacity)
                  : (resource.weeklyCapacity || 0);
                
                // Calculate resource utilization
                // Find all tasks assigned to this resource in current week/sprint (exclude Done/Archived)
                const resourceTasks = sprintTasks.filter((task: any) => {
                  const taskStatus = task.status?.toLowerCase() || '';
                  if (task.projectId && !filteredProjectIds.has(task.projectId)) {
                    return false;
                  }
                  return task.contributors?.includes(resource.id) &&
                    !taskStatus.includes('done') &&
                    !taskStatus.includes('archived') &&
                    !taskStatus.includes('completed');
                });
                
                // Calculate remaining hours for this resource
                const remainingHours = resourceTasks.reduce((sum: number, task: any) => {
                  const estimated = task.estimatedHours || 0;
                  const realised = task.realisedHours || 0;
                  return sum + Math.max(0, estimated - realised);
                }, 0);
                
                // Utilization = remaining hours / weekly capacity
                const utilization = capacity > 0 ? Math.round((remainingHours / capacity) * 100) : 0;
                
                return (
                  <Card key={resource.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm truncate">{resource.name}</h4>
                          <p className="text-xs text-muted-foreground truncate">{resource.email}</p>
                        </div>
                        <div className="text-right ml-3">
                          <p className="text-xl font-bold text-primary">{(capacity / 8).toFixed(1)}d</p>
                          <p className="text-xs text-muted-foreground whitespace-nowrap">{capacity}h/week</p>
                        </div>
                      </div>
                      
                      {/* Utilization Bar */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">Utilization</span>
                          <span className={`text-xs font-semibold ${
                            utilization > 100 ? 'text-red-600' :
                            utilization >= 80 ? 'text-orange-600' :
                            'text-green-600'
                          }`}>
                            {utilization}%
                          </span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${
                              utilization > 100 ? 'bg-red-500' :
                              utilization >= 80 ? 'bg-orange-500' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(100, utilization)}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {remainingHours.toFixed(1)}h remaining
                        </p>
                      </div>
                      
                      {Array.isArray(resource.department) && resource.department.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {resource.department.map(dept => (
                            <Badge key={dept} variant="secondary" className="text-xs">
                              {dept}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Task Dialog */}
      <Dialog open={isAddTaskDialogOpen} onOpenChange={handleTaskDialogChange}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Task</DialogTitle>
            <DialogDescription>
              Capture the essentials so the team knows what to tackle next. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="taskTitle">Task Title *</Label>
              <Input
                id="taskTitle"
                placeholder="e.g., Kick-off deck"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Project *</Label>
                {projects.length === 0 ? (
                  <Alert>
                    <AlertDescription>No projects available. Create a project first.</AlertDescription>
                  </Alert>
                ) : (
                  <Popover open={isProjectPickerOpen} onOpenChange={(open) => {
                    setIsProjectPickerOpen(open);
                    if (!open) setProjectSearchTerm('');
                  }}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={isProjectPickerOpen}
                        className="w-full justify-between"
                      >
                        {selectedProject ? (
                          <div className="flex flex-col text-left">
                            <span className="font-medium leading-tight">{selectedProject.name}</span>
                            {selectedProject.projectCode && (
                              <span className="text-xs text-muted-foreground">{selectedProject.projectCode}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Search for a project</span>
                        )}
                        <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[320px]" align="start">
                      <Command>
                        <CommandInput
                          placeholder="Type to filter projects..."
                          value={projectSearchTerm}
                          onChange={(e) => setProjectSearchTerm(e.target.value)}
                        />
                        <CommandList>
                          {taskPickerProjects.length === 0 && (
                            <CommandEmpty>No projects found.</CommandEmpty>
                          )}
                          <CommandGroup>
                            {taskPickerProjects.map(project => (
                              <CommandItem
                                key={project.id}
                                onSelect={() => handleSelectProject(project.id)}
                                className={cn('flex items-center justify-between gap-3',
                                  taskProjectId === project.id && 'bg-accent text-accent-foreground')}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium leading-tight">{project.name}</span>
                                  {project.projectCode && (
                                    <span className="text-xs text-muted-foreground">{project.projectCode}</span>
                                  )}
                                </div>
                                {taskProjectId === project.id && (
                                  <Check className="h-4 w-4" />
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="taskStatus">Status</Label>
                <Select value={taskStatus} onValueChange={setTaskStatus}>
                  <SelectTrigger id="taskStatus">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="taskPriority">Priority</Label>
                <Select value={taskPriority} onValueChange={setTaskPriority}>
                  <SelectTrigger id="taskPriority">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_PRIORITY_OPTIONS.map((priority) => (
                      <SelectItem key={priority} value={priority}>
                        {priority}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="taskDueDate">Due Date</Label>
                <Input
                  id="taskDueDate"
                  type="date"
                  value={taskDueDate}
                  onChange={(e) => setTaskDueDate(e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="taskEstimatedHours">Estimated Hours</Label>
                <Input
                  id="taskEstimatedHours"
                  type="number"
                  min="0"
                  step="0.25"
                  placeholder="e.g., 6"
                  value={taskEstimatedHours}
                  onChange={(e) => setTaskEstimatedHours(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="taskSprint">Sprint</Label>
              {sortedSprintsDesc.length === 0 ? (
                <p className="text-sm text-muted-foreground">No sprints are available right now.</p>
              ) : (
                <Select value={taskSprintId} onValueChange={setTaskSprintId}>
                  <SelectTrigger id="taskSprint">
                    <SelectValue placeholder="Select sprint" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedSprintsDesc.map(sprint => (
                      <SelectItem key={sprint.id} value={sprint.id}>
                        <div className="flex flex-col">
                          <span className="font-medium leading-tight">{sprint.name || 'Unnamed sprint'}</span>
                          {(sprint.startDate || sprint.endDate) && (
                            <span className="text-xs text-muted-foreground">
                              {[sprint.startDate, sprint.endDate].filter(Boolean).join(' → ')}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              {allTags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {allTags.map(tag => {
                    const tagColors = getTagColors(tag);
                    const isSelected = taskTags.includes(tag);
                    return (
                      <Badge
                        key={tag}
                        className={`cursor-pointer border ${isSelected ? `${tagColors.bg} ${tagColors.text} ${tagColors.border}` : 'bg-background text-muted-foreground'}`}
                        onClick={() => toggleTaskTag(tag)}
                      >
                        {tag}
                      </Badge>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No tags detected from current tasks.</p>
              )}
              {taskTags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {taskTags.map(tag => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => toggleTaskTag(tag)}
                    >
                      {tag} <span className="ml-1">×</span>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Assignees (Notion users)</Label>
              {loadingUsers ? (
                <div className="flex items-center gap-2 p-3 border rounded-md">
                  <Spinner className="h-4 w-4" />
                  <span className="text-sm text-muted-foreground">Loading users...</span>
                </div>
              ) : users.length === 0 ? (
                <p className="text-sm text-muted-foreground">No Notion users available.</p>
              ) : (
                <div className="border rounded-md max-h-[220px] overflow-y-auto divide-y">
                  {users
                    .filter(user => user.email)
                    .map(user => {
                      const email = user.email as string;
                      const isSelected = taskAssignees.includes(email);
                      return (
                        <div
                          key={user.id}
                          className={`p-3 cursor-pointer flex items-center justify-between ${isSelected ? 'bg-accent' : 'hover:bg-accent/60'}`}
                          onClick={() => toggleTaskAssignee(email)}
                        >
                          <div className="flex items-center gap-3">
                            <NotionAvatar
                              name={user.name}
                              profileImage={user.profileImage}
                              className="h-8 w-8"
                            />
                            <div>
                              <p className="font-medium">{user.name}</p>
                              <p className="text-xs text-muted-foreground">{email}</p>
                            </div>
                          </div>
                          {isSelected && <Badge variant="default" className="text-xs">Selected</Badge>}
                        </div>
                      );
                    })}
                </div>
              )}
              {taskAssignees.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {taskAssignees.map(email => {
                    const user = users.find(u => u.email === email);
                    return (
                      <Badge
                        key={email}
                        variant="secondary"
                        className="cursor-pointer flex items-center gap-1"
                        onClick={() => toggleTaskAssignee(email)}
                      >
                        {user ? (
                          <>
                            <NotionAvatar
                              name={user.name}
                              profileImage={user.profileImage}
                              className="h-4 w-4"
                            />
                            <span>{user.name}</span>
                          </>
                        ) : (
                          <span>{email}</span>
                        )}
                        <span className="ml-1">×</span>
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Contributors</Label>
              {loadingResources ? (
                <div className="flex items-center gap-2 p-3 border rounded-md">
                  <Spinner className="h-4 w-4" />
                  <span className="text-sm text-muted-foreground">Loading resources...</span>
                </div>
              ) : resources.length === 0 ? (
                <p className="text-sm text-muted-foreground">No resources available.</p>
              ) : (
                <div className="border rounded-md max-h-[220px] overflow-y-auto divide-y">
                  {resources.map(resource => {
                    const isSelected = taskContributors.includes(resource.id);
                    return (
                      <div
                        key={resource.id}
                        className={`p-3 cursor-pointer flex items-center justify-between ${isSelected ? 'bg-accent' : 'hover:bg-accent/60'}`}
                        onClick={() => toggleTaskContributor(resource.id)}
                      >
                        <div>
                          <p className="font-medium">{resource.name}</p>
                          <p className="text-xs text-muted-foreground">{resource.email}</p>
                          {Array.isArray(resource.department) && resource.department.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {resource.department.map(dept => (
                                <Badge key={dept} variant="outline" className="text-[10px]">
                                  {dept}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        {isSelected && <Badge variant="default" className="text-xs">Selected</Badge>}
                      </div>
                    );
                  })}
                </div>
              )}
              {taskContributors.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {taskContributors.map(id => {
                    const resource = resources.find(r => r.id === id);
                    return resource ? (
                      <Badge
                        key={id}
                        variant="secondary"
                        className="cursor-pointer"
                        onClick={() => toggleTaskContributor(id)}
                      >
                        {resource.name}
                        <span className="ml-1">×</span>
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              type="button"
              onClick={() => handleTaskDialogChange(false)}
              disabled={taskSubmitting}
            >
              Cancel
            </Button>
            <Button 
              type="button"
              onClick={handleCreateTask}
              disabled={taskSubmitting || !taskTitle.trim() || !taskProjectId}
            >
              {taskSubmitting ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Project Dialog */}
      <Dialog open={isAddProjectDialogOpen} onOpenChange={setIsAddProjectDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {projectCreationStep === 'company' ? 'Add New Project' : 'Create Project Details'}
            </DialogTitle>
            <DialogDescription>
              {projectCreationStep === 'company' 
                ? 'Search for a company to add a new project'
                : `Creating project for ${selectedCompany?.name}`
              }
            </DialogDescription>
          </DialogHeader>
          
          {projectCreationStep === 'company' ? (
          <>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="companySearch">Company Name</Label>
              <div className="flex gap-2">
                <Input
                  id="companySearch"
                  placeholder="Enter company name..."
                  value={companySearchQuery}
                  onChange={(e) => setCompanySearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchCompany()}
                />
                <Button onClick={searchCompany} disabled={isSearchingCompany || !companySearchQuery.trim()}>
                  {isSearchingCompany ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Search Results */}
            {companySearchResults.length > 0 && (
              <div className="space-y-2">
                <Label>Select Company</Label>
                <div className="border rounded-md max-h-[300px] overflow-y-auto">
                  {companySearchResults.map((company) => (
                    <div
                      key={company.id}
                      className={`p-3 cursor-pointer hover:bg-accent transition-colors border-b last:border-b-0 ${
                        selectedCompany?.id === company.id ? 'bg-accent' : ''
                      }`}
                      onClick={() => handleSelectCompany(company)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{company.name}</span>
                          </div>
                          {company.contactEmail && (
                            <p className="text-sm text-muted-foreground">{company.contactEmail}</p>
                          )}
                          {company.phone && (
                            <p className="text-sm text-muted-foreground">{company.phone}</p>
                          )}
                          <div className="flex gap-2 mt-2 text-xs text-muted-foreground">
                            {company.projects && (
                              <span>{company.projects.length} project{company.projects.length !== 1 ? 's' : ''}</span>
                            )}
                            {company.contacts && (
                              <span>• {company.contacts.length} contact{company.contacts.length !== 1 ? 's' : ''}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Company Not Found */}
            {companyNotFound && (
              <Alert>
                <AlertDescription className="flex items-center justify-between">
                  <span>Company not found in Notion</span>
                  <Button onClick={handleAddCompanyToNotion} size="sm" variant="outline">
                    Add Company to Notion
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Selected Company Info */}
            {selectedCompany && (
              <div className="p-4 border rounded-lg bg-accent/50">
                <h4 className="font-semibold mb-2">Selected Company:</h4>
                <p className="text-sm">{selectedCompany.name}</p>
                {selectedCompany.contactEmail && (
                  <p className="text-sm text-muted-foreground">{selectedCompany.contactEmail}</p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddProjectDialogOpen(false)}>
              Cancel
            </Button>
            {selectedCompany && (
              <Button 
                onClick={() => setProjectCreationStep('project')}
              >
                Continue
              </Button>
            )}
          </DialogFooter>
          </>
          ) : (
          <>
          <div className="space-y-4 py-4">
            {/* Project Name */}
            <div className="space-y-2">
              <Label htmlFor="projectName">Project Name *</Label>
              <Input
                id="projectName"
                placeholder="Enter project name..."
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />
            </div>

            {/* Project Code */}
            <div className="space-y-2">
              <Label htmlFor="projectCode">Project Code *</Label>
              <Input
                id="projectCode"
                placeholder="e.g., PRJ-001"
                value={projectCode}
                onChange={(e) => setProjectCode(e.target.value)}
              />
            </div>

            {/* Budget */}
            <div className="space-y-2">
              <Label htmlFor="projectBudget">Budget Sold *</Label>
              <Input
                id="projectBudget"
                type="number"
                placeholder="Enter budget amount..."
                value={projectBudget}
                onChange={(e) => setProjectBudget(e.target.value)}
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="projectStatus">Status *</Label>
              <Select value={projectStatus} onValueChange={setProjectStatus}>
                <SelectTrigger id="projectStatus">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Planning">Planning</SelectItem>
                  <SelectItem value="Backlog">Backlog</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                  <SelectItem value="Paused">Paused</SelectItem>
                  <SelectItem value="Done">Done</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label htmlFor="projectPriority">Priority</Label>
              <Select value={projectPriority} onValueChange={setProjectPriority}>
                <SelectTrigger id="projectPriority">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="projectStartDate">Start Date</Label>
                <Input
                  id="projectStartDate"
                  type="date"
                  value={projectStartDate}
                  onChange={(e) => setProjectStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="projectEndDate">End Date</Label>
                <Input
                  id="projectEndDate"
                  type="date"
                  value={projectEndDate}
                  onChange={(e) => setProjectEndDate(e.target.value)}
                />
              </div>
            </div>

            {/* GDrive Room */}
            <div className="space-y-2">
              <Label htmlFor="projectGDriveRoom">Google Drive Room (Optional)</Label>
              <Input
                id="projectGDriveRoom"
                placeholder="https://drive.google.com/..."
                value={projectGDriveRoom}
                onChange={(e) => setProjectGDriveRoom(e.target.value)}
              />
            </div>

            {/* Owner Selection */}
            <div className="space-y-2">
              <Label htmlFor="projectOwner">Owner *</Label>
              {loadingUsers ? (
                <div className="flex items-center gap-2 p-2 border rounded-md">
                  <Spinner className="h-4 w-4" />
                  <span className="text-sm text-muted-foreground">Loading users...</span>
                </div>
              ) : (
                <Select value={projectOwner} onValueChange={setProjectOwner}>
                  <SelectTrigger id="projectOwner">
                    <SelectValue placeholder="Select project owner" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          {user.profileImage && (
                            <img src={user.profileImage} alt={user.name} className="w-5 h-5 rounded-full" />
                          )}
                          <span>{user.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Collaborators Selection */}
            <div className="space-y-2">
              <Label>Collaborators (Optional)</Label>
              {loadingUsers ? (
                <div className="flex items-center gap-2 p-2 border rounded-md">
                  <Spinner className="h-4 w-4" />
                  <span className="text-sm text-muted-foreground">Loading users...</span>
                </div>
              ) : (
                <>
                  <div className="border rounded-md max-h-[200px] overflow-y-auto">
                    {users.map((user) => {
                      const isSelected = projectCollaborators.includes(user.id);
                      const isOwner = projectOwner === user.id;
                      return (
                        <div
                          key={user.id}
                          className={`p-3 cursor-pointer hover:bg-accent transition-colors border-b last:border-b-0 ${
                            isSelected ? 'bg-accent' : ''
                          } ${isOwner ? 'opacity-50 cursor-not-allowed' : ''}`}
                          onClick={() => {
                            if (isOwner) return;
                            setProjectCollaborators(prev =>
                              isSelected
                                ? prev.filter(id => id !== user.id)
                                : [...prev, user.id]
                            );
                          }}
                        >
                          <div className="flex items-center gap-3">
                            {user.profileImage && (
                              <img src={user.profileImage} alt={user.name} className="w-8 h-8 rounded-full" />
                            )}
                            <div className="flex-1">
                              <p className="font-medium">{user.name}</p>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                            </div>
                            {isSelected && <Badge variant="default" className="text-xs">Selected</Badge>}
                            {isOwner && <Badge variant="secondary" className="text-xs">Owner</Badge>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {projectCollaborators.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {projectCollaborators.map((userId) => {
                        const user = users.find(u => u.id === userId);
                        return user ? (
                          <Badge 
                            key={userId} 
                            variant="secondary"
                            className="cursor-pointer"
                            onClick={() => setProjectCollaborators(prev => prev.filter(id => id !== userId))}
                          >
                            {user.name}
                            <span className="ml-1">×</span>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Selected Company Info */}
            {selectedCompany && (
              <Alert>
                <AlertDescription>
                  <p className="font-medium mb-1">Company: {selectedCompany.name}</p>
                  {selectedCompany.contactEmail && (
                    <p className="text-sm text-muted-foreground">{selectedCompany.contactEmail}</p>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setProjectCreationStep('company')}
            >
              Back
            </Button>
            <Button 
              onClick={handleCreateProject}
              disabled={!projectName.trim() || !projectCode.trim() || !projectBudget.trim() || !projectOwner || isCreatingProject}
            >
              {isCreatingProject ? 'Creating...' : 'Create Project'}
            </Button>
          </DialogFooter>
          </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Company Dialog */}
      <Dialog open={isAddCompanyDialogOpen} onOpenChange={setIsAddCompanyDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {projectCreationStep === 'company' ? 'Select or Add Company' : 'Create New Project'}
            </DialogTitle>
            <DialogDescription>
              {projectCreationStep === 'company' 
                ? 'Search for an existing company or create a new one'
                : `Creating project for ${selectedCompany?.name || 'selected company'}`
              }
            </DialogDescription>
          </DialogHeader>
          
          {projectCreationStep === 'company' ? (
          <>
          <div className="space-y-4 py-4">
            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name *</Label>
              <Input
                id="companyName"
                placeholder="Enter company name..."
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
              />
            </div>

            {/* Contact Email */}
            <div className="space-y-2">
              <Label htmlFor="companyContactEmail">Contact Email *</Label>
              <Input
                id="companyContactEmail"
                type="email"
                placeholder="Enter contact email..."
                value={newCompanyEmail}
                onChange={(e) => setNewCompanyEmail(e.target.value)}
              />
            </div>

            {/* Contact Search */}
            <div className="space-y-2">
              <Label htmlFor="contactSearch">Search Contacts</Label>
              <div className="flex gap-2">
                <Input
                  id="contactSearch"
                  placeholder="Search by contact name..."
                  value={contactSearchQuery}
                  onChange={(e) => setContactSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchContact()}
                />
                <Button onClick={searchContact} disabled={isSearchingContact || !contactSearchQuery.trim()}>
                  {isSearchingContact ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Contact Search Results */}
            {contactSearchResults.length > 0 && (
              <div className="space-y-2">
                <Label>Available Contacts (click to select)</Label>
                <div className="border rounded-md max-h-[200px] overflow-y-auto">
                  {contactSearchResults.map((contact) => {
                    const isSelected = selectedContacts.find(c => c.id === contact.id);
                    return (
                      <div
                        key={contact.id}
                        className={`p-3 cursor-pointer hover:bg-accent transition-colors border-b last:border-b-0 ${
                          isSelected ? 'bg-accent' : ''
                        }`}
                        onClick={() => handleSelectContact(contact)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{contact.name}</span>
                              {isSelected && (
                                <Badge variant="default" className="text-xs">Selected</Badge>
                              )}
                            </div>
                            {contact.email && (
                              <p className="text-sm text-muted-foreground">{contact.email}</p>
                            )}
                            {contact.phone && (
                              <p className="text-sm text-muted-foreground">{contact.phone}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Contact Not Found */}
            {contactNotFound && (
              <Alert>
                <AlertDescription className="flex items-center justify-between">
                  <span>Contact not found in Notion</span>
                  <Button onClick={handleAddContactToNotion} size="sm" variant="outline">
                    Add Contact to Notion
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Selected Contacts */}
            {selectedContacts.length > 0 && (
              <div className="space-y-2">
                <Label>Selected Contacts ({selectedContacts.length})</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedContacts.map((contact) => (
                    <Badge 
                      key={contact.id} 
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => handleSelectContact(contact)}
                    >
                      {contact.name}
                      <span className="ml-1">×</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddCompanyDialogOpen(false)}>
              Cancel
            </Button>
            {selectedCompany ? (
              <Button 
                onClick={() => setProjectCreationStep('project')}
              >
                Continue to Project
              </Button>
            ) : (
              <Button 
                onClick={handleSubmitCompany}
                disabled={!newCompanyName.trim() || !newCompanyEmail.trim() || selectedContacts.length === 0 || isCreatingCompany}
              >
                {isCreatingCompany ? 'Creating...' : 'Create Company & Continue'}
              </Button>
            )}
          </DialogFooter>
          </>
          ) : (
          <>
          <div className="space-y-4 py-4">
            {/* Project Name */}
            <div className="space-y-2">
              <Label htmlFor="projectName">Project Name *</Label>
              <Input
                id="projectName"
                placeholder="Enter project name..."
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />
            </div>

            {/* Project Code */}
            <div className="space-y-2">
              <Label htmlFor="projectCode">Project Code *</Label>
              <Input
                id="projectCode"
                placeholder="e.g., PRJ-001"
                value={projectCode}
                onChange={(e) => setProjectCode(e.target.value)}
              />
            </div>

            {/* Budget */}
            <div className="space-y-2">
              <Label htmlFor="projectBudget">Budget Sold *</Label>
              <Input
                id="projectBudget"
                type="number"
                placeholder="Enter budget amount..."
                value={projectBudget}
                onChange={(e) => setProjectBudget(e.target.value)}
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label htmlFor="projectStatus">Status *</Label>
              <Select value={projectStatus} onValueChange={setProjectStatus}>
                <SelectTrigger id="projectStatus">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Planning">Planning</SelectItem>
                  <SelectItem value="Backlog">Backlog</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                  <SelectItem value="Paused">Paused</SelectItem>
                  <SelectItem value="Done">Done</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label htmlFor="projectPriority">Priority</Label>
              <Select value={projectPriority} onValueChange={setProjectPriority}>
                <SelectTrigger id="projectPriority">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="projectStartDate">Start Date</Label>
                <Input
                  id="projectStartDate"
                  type="date"
                  value={projectStartDate}
                  onChange={(e) => setProjectStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="projectEndDate">End Date</Label>
                <Input
                  id="projectEndDate"
                  type="date"
                  value={projectEndDate}
                  onChange={(e) => setProjectEndDate(e.target.value)}
                />
              </div>
            </div>

            {/* GDrive Room */}
            <div className="space-y-2">
              <Label htmlFor="projectGDriveRoom">Google Drive Room (Optional)</Label>
              <Input
                id="projectGDriveRoom"
                placeholder="https://drive.google.com/..."
                value={projectGDriveRoom}
                onChange={(e) => setProjectGDriveRoom(e.target.value)}
              />
            </div>

            {/* Selected Company Info */}
            {selectedCompany && (
              <Alert>
                <AlertDescription>
                  <p className="font-medium mb-1">Company: {selectedCompany.name}</p>
                  {selectedCompany.email && (
                    <p className="text-sm text-muted-foreground">{selectedCompany.email}</p>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setProjectCreationStep('company')}
            >
              Back
            </Button>
            <Button 
              onClick={handleCreateProject}
              disabled={!projectName.trim() || !projectCode.trim() || !projectBudget.trim() || isCreatingProject}
            >
              {isCreatingProject ? 'Creating...' : 'Create Project'}
            </Button>
          </DialogFooter>
          </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={isAddContactDialogOpen} onOpenChange={setIsAddContactDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Contact to Notion</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  placeholder="Enter first name"
                  value={newContactFirstName}
                  onChange={(e) => setNewContactFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  placeholder="Enter last name"
                  value={newContactLastName}
                  onChange={(e) => setNewContactLastName(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="contact@example.com"
                value={newContactEmail}
                onChange={(e) => {
                  setNewContactEmail(e.target.value);
                  setExistingContactConflict(null);
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone (Optional)</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 234 567 8900"
                value={newContactPhone}
                onChange={(e) => setNewContactPhone(e.target.value)}
              />
            </div>

            {/* Email Conflict Alert */}
            {existingContactConflict && (
              <Alert>
                <AlertDescription>
                  <div className="space-y-3">
                    <p className="font-medium">This email is already in use by an existing contact:</p>
                    <div className="p-3 bg-muted rounded-md">
                      <p className="font-semibold">{existingContactConflict.displayName || existingContactConflict.name}</p>
                      <p className="text-sm text-muted-foreground">{existingContactConflict.email}</p>
                      {existingContactConflict.phone && (
                        <p className="text-sm text-muted-foreground">{existingContactConflict.phone}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleSelectExistingContact} size="sm" variant="default">
                        Use This Contact
                      </Button>
                      <Button 
                        onClick={() => setExistingContactConflict(null)} 
                        size="sm" 
                        variant="outline"
                      >
                        Change Email
                      </Button>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddContactDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateContact}
              disabled={!newContactFirstName.trim() || !newContactLastName.trim() || !newContactEmail.trim() || isCreatingContact}
            >
              {isCreatingContact ? 'Creating...' : 'Create Contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint Assignment Dialog */}
      <Dialog open={isSprintAssignDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsSprintAssignDialogOpen(false);
          setTaskToAssign(null);
          setSelectedSprintForAssign('');
          setSprintSearchTerm('');
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Assign Sprint</DialogTitle>
            <DialogDescription>
              {taskToAssign && (
                <span>Assigning sprint to: <strong>{taskToAssign.title}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Sprint</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between"
                  >
                    {selectedSprintForAssign ? (
                      sprints.find(s => s.id === selectedSprintForAssign)?.name || 'Unknown Sprint'
                    ) : (
                      <span className="text-muted-foreground">Search for a sprint...</span>
                    )}
                    <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[400px]" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Type to filter sprints..."
                      value={sprintSearchTerm}
                      onChange={(e) => setSprintSearchTerm(e.target.value)}
                    />
                    <CommandList>
                      <CommandEmpty>No sprints found.</CommandEmpty>
                      {/* Active Sprints */}
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Active Sprints</div>
                      <CommandGroup>
                        {sprints
                          .filter(s => {
                            const status = s.status?.toLowerCase() || '';
                            return status === 'current' || status === 'next' || status === 'active';
                          })
                          .filter(s => 
                            !sprintSearchTerm || 
                            s.name.toLowerCase().includes(sprintSearchTerm.toLowerCase())
                          )
                          .map(sprint => (
                            <CommandItem
                              key={sprint.id}
                              onSelect={() => setSelectedSprintForAssign(sprint.id)}
                              className={cn('flex items-center justify-between gap-3',
                                selectedSprintForAssign === sprint.id && 'bg-accent text-accent-foreground')}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{sprint.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {sprint.startDate && sprint.endDate 
                                    ? `${sprint.startDate} - ${sprint.endDate}` 
                                    : sprint.status || 'No dates'}
                                </span>
                              </div>
                              {selectedSprintForAssign === sprint.id && (
                                <Check className="h-4 w-4" />
                              )}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                      {/* Other Sprints */}
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Other Sprints</div>
                      <CommandGroup>
                        {sprints
                          .filter(s => {
                            const status = s.status?.toLowerCase() || '';
                            return status !== 'current' && status !== 'next' && status !== 'active';
                          })
                          .filter(s => 
                            !sprintSearchTerm || 
                            s.name.toLowerCase().includes(sprintSearchTerm.toLowerCase())
                          )
                          .map(sprint => (
                            <CommandItem
                              key={sprint.id}
                              onSelect={() => setSelectedSprintForAssign(sprint.id)}
                              className={cn('flex items-center justify-between gap-3',
                                selectedSprintForAssign === sprint.id && 'bg-accent text-accent-foreground')}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{sprint.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {sprint.startDate && sprint.endDate 
                                    ? `${sprint.startDate} - ${sprint.endDate}` 
                                    : sprint.status || 'No dates'}
                                </span>
                              </div>
                              {selectedSprintForAssign === sprint.id && (
                                <Check className="h-4 w-4" />
                              )}
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsSprintAssignDialogOpen(false);
              setTaskToAssign(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={async () => {
                if (!taskToAssign || !selectedSprintForAssign) return;
                
                setIsAssigningSprint(true);
                try {
                  await assignTaskSprint(taskToAssign.id, selectedSprintForAssign);
                  toast({
                    title: 'Sprint Assigned',
                    description: `Task has been assigned to sprint successfully.`,
                  });
                  setIsSprintAssignDialogOpen(false);
                  setTaskToAssign(null);
                  // Refresh sprint tasks to update the UI
                  await refreshSprints(true);
                } catch (error) {
                  console.error('Failed to assign sprint:', error);
                  toast({
                    title: 'Error',
                    description: 'Failed to assign sprint to task. Please try again.',
                  });
                } finally {
                  setIsAssigningSprint(false);
                }
              }}
              disabled={!selectedSprintForAssign || isAssigningSprint}
            >
              {isAssigningSprint ? 'Assigning...' : 'Assign Sprint'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
